<template>
  <DashboardLayout :lastUpdated="lastUpdated">
    <template slot="navbarFilters">
      <div v-if="hasWorldStore" class="navbar-filters">
        <div v-if="sellerAccountsList && sellerAccountsList.length > 0" class="ui dropdown filter-dropdown navbar-dropdown" ref="saDropdown">
          <div class="dropdown-trigger">
            <span class="text">S.Accounts: {{ filterBySellAcc.length === 0 ? 'All' : filterBySellAcc.length + ' selected' }}</span>
            <i class="dropdown icon"></i>
          </div>
          <div class="menu">
            <div class="item" :class="{ selected: filterBySellAcc.length === 0 }" data-value="">
              <i class="check icon" v-if="filterBySellAcc.length === 0"></i>
              All
            </div>
            <div class="divider"></div>
            <div v-for="sa in sellerAccountsList" :key="String(sa.sellerId)" class="item" :class="{ selected: isSellerSelected(sa.sellerId) }" :data-value="sa.sellerId">
              <i class="check icon" v-if="isSellerSelected(sa.sellerId)"></i>
              {{ sa.accountNickName }}
            </div>
          </div>
        </div>
        <div v-if="filterUserCountries && filterUserCountries.length > 0" class="ui dropdown filter-dropdown navbar-dropdown" ref="marketplaceDropdown">
          <div class="dropdown-trigger">
            <span class="text">Marketplaces: {{ filterByMarket.length === 0 ? 'All' : filterByMarket.length === 1 ? filterByMarket[0] : filterByMarket.length + ' selected' }}</span>
            <i class="dropdown icon"></i>
          </div>
          <div class="menu">
            <div class="item" :class="{ selected: filterByMarket.length === 0 }" data-value="">
              <i class="check icon" v-if="filterByMarket.length === 0"></i>
              All
            </div>
            <div class="divider"></div>
            <div v-for="c in filteredCountries" :key="c" :class="[c === 's' ? 'divider' : 'item', { selected: filterByMarket.indexOf(c) >= 0 && c !== 's' }]" :data-value="c">
              <i class="check icon" v-if="filterByMarket.indexOf(c) >= 0 && c !== 's'"></i>
              <i v-if="c !== 's'" :class="c.toLowerCase().replace('uk', 'gb') + ' flag'" style="margin-right: 8px;"></i>
              <span v-if="c !== 's'">Amazon.{{ getMarketplaceSuffix(c) }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  <div class="all-alerts-page">
    <div class="alerts-content">
      <div v-if="loading" class="loading-state">
        <i class="spinner loading icon"></i>
        <p>Loading issues...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <i class="exclamation triangle icon"></i>
        <h3>Failed to load issues</h3>
        <p>{{ error }}</p>
        <button class="ui primary button" @click="fetchIssues">Retry</button>
      </div>

      <template v-else>
      <div class="alerts-header">
        <div class="header-info">
          <h2 class="section-title">Issues</h2>
          <span class="total-count">{{ totalAlerts }} total alerts</span>
        </div>
        <div class="header-actions">
          <div class="filter-group">
            <select v-model="filterStatus" class="filter-select">
              <option value="all">All Status</option>
              <option value="suppressed">Suppressed</option>
              <option value="lost">Lost to Competitor</option>
              <option value="contested">Contested</option>
              <option value="owned">Owned</option>
            </select>
          </div>
          <input
            v-model="searchQuery"
            v-prevent-autofill="searchQuery"
            type="search"
            placeholder="Search alerts..."
            class="search-input"
          >
          <button class="export-button" :disabled="exporting" @click="exportCsv">
            <i class="download icon"></i>
            {{ exporting ? 'Exporting…' : 'Export CSV' }}
          </button>
        </div>
      </div>

      <!-- Alerts Table -->
      <div class="alerts-table">
        <table>
          <thead>
            <tr>
              <th>PRODUCT</th>
              <th class="th-sort">
                <span
                  class="sort-col-head"
                  :class="sortHeadClass('status')"
                  role="button"
                  tabindex="0"
                  title="Sort by Status"
                  @click.stop="onSort('status')"
                  @keydown.enter.prevent="onSort('status')"
                >
                  STATUS
                  <span class="sort-col-icons" aria-hidden="true">
                    <svg v-if="sortBy === 'status' && sortDir === 'asc'" class="sort-icon-filled" viewBox="0 0 24 24"><path fill="currentColor" d="M12 8l7 9H5z"/></svg>
                    <svg v-else-if="sortBy === 'status' && sortDir === 'desc'" class="sort-icon-filled" viewBox="0 0 24 24"><path fill="currentColor" d="M12 16l-7-9h14z"/></svg>
                    <svg v-else class="sort-icon-neutral" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 15l5 5 5-5M7 9l5-5 5 5"/></svg>
                  </span>
                </span>
              </th>
              <th class="th-sort">
                <span
                  class="sort-col-head"
                  :class="sortHeadClass('impact')"
                  role="button"
                  tabindex="0"
                  title="Sort by Est. Daily"
                  @click.stop="onSort('impact')"
                  @keydown.enter.prevent="onSort('impact')"
                >
                  EST. DAILY
                  <span class="sort-col-icons" aria-hidden="true">
                    <svg v-if="sortBy === 'impact' && sortDir === 'asc'" class="sort-icon-filled" viewBox="0 0 24 24"><path fill="currentColor" d="M12 8l7 9H5z"/></svg>
                    <svg v-else-if="sortBy === 'impact' && sortDir === 'desc'" class="sort-icon-filled" viewBox="0 0 24 24"><path fill="currentColor" d="M12 16l-7-9h14z"/></svg>
                    <svg v-else class="sort-icon-neutral" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 15l5 5 5-5M7 9l5-5 5 5"/></svg>
                  </span>
                </span>
              </th>
              <th>ACTION</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(alert, index) in paginatedAlerts"
              :key="index"
              class="alert-row"
            >
              <td class="product-cell" @click="viewProductDetails(alert.productId || alert.id)">
                <div class="product-image-wrap">
                  <div class="product-image-placeholder"></div>
                  <img
                    v-if="alert.imageUrl"
                    :src="alert.imageUrl"
                    :alt="alert.product"
                    class="product-image"
                    @error="e => e.target.style.display = 'none'"
                  />
                </div>
                <div class="product-info">
                  <div class="product-name-line">
                    <span class="product-name clickable">{{ alert.product }}</span>
                  </div>
                  <span v-if="alert.asin" class="product-asin">{{ alert.asin }}</span>
                </div>
              </td>
              <td class="status-cell">
                <span class="alert-status" :class="alert.statusType">{{ alert.status }}</span>
              </td>
              <td class="impact-cell">
                  <span class="impact-value" :class="alert.impactClass">{{ alert.impact }}</span>
              </td>
              <td class="action-cell">
                <a class="view-details-link" @click.stop="handleActionButton(alert)">
                  View Details >
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination">
        <div class="pagination-info">
          Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ totalAlerts }} alerts
        </div>
        <div class="pagination-controls">
          <button
            @click="previousPage"
            :disabled="currentPage === 1"
            class="pagination-button"
          >
            <i class="chevron left icon"></i>
            Previous
          </button>
          <div class="pagination-numbers">
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="goToPage(page)"
              :class="['page-number', { active: page === currentPage }]"
            >
              {{ page }}
            </button>
          </div>
          <button
            @click="nextPage"
            :disabled="currentPage >= totalPages"
            class="pagination-button"
          >
            Next
            <i class="chevron right icon"></i>
          </button>
        </div>
      </div>
      </template>
    </div>
  </div>
  </DashboardLayout>
</template>

<script>
import $ from 'jquery'
import { extractV1SellersItems, mapV1SellersToFilterAccounts, buildMarketplaceFilterCodes, buildMarketplaceFilterDropdownItems, MARKETPLACE_FILTER_COUNTRIES_ALL, getMarketplaceAmazonSuffix } from '../utils/sellerAccountsFromV1Api.js'
import './stock/styles/StockDashboard.css'
import './buybox/styles/AllAlertsPage.css'
import DashboardLayout from './buybox/DashboardLayout.vue'
import { buildProductDetailsQuery } from '../utils/listViewUrlSync.js'

var ALERTS_URL_KEYS = ['marketplaces', 'sellerAccounts']
var ALERTS_TABLE_SORT_KEYS = ['status', 'impact']

function shallowEqualAlertsQuery (next, routeQuery) {
  for (var i = 0; i < ALERTS_URL_KEYS.length; i++) {
    var k = ALERTS_URL_KEYS[i]
    var nv = next[k] != null ? String(next[k]) : ''
    var rv = routeQuery && routeQuery[k] != null ? String(routeQuery[k]) : ''
    if (nv !== rv) return false
  }
  return true
}

export default {
  name: 'AllAlertsPage',
  components: {
    DashboardLayout
  },
  data: function () {
    return {
      loading: true,
      error: null,
      lastUpdated: 'just now',
      lastUpdateTime: null,
      timerInterval: null,
      searchQuery: '',
      filterStatus: 'all',
      sortBy: 'impact',
      sortDir: 'desc',
      currentPage: 1,
      itemsPerPage: 10,
      totalAlerts: 0,
      totalPages: 1,
      allAlerts: [],
      searchTimeout: null,
      filterByMarket: [],
      filterBySellAcc: [],
      filterCountriesAll: MARKETPLACE_FILTER_COUNTRIES_ALL,
      filterUserCountries: [],
      buyboxFilterData: { availableFilters: null },
      loadingFilters: false,
      exporting: false,
      _syncingDropdowns: false,
      _applyingAlertsRouteQuery: false
    }
  },
  computed: {
    worldStore: function () {
      return this.$parent && this.$parent.$parent && this.$parent.$parent.worldStore
    },
    hasWorldStore: function () {
      return !!(this.worldStore && this.worldStore.products !== undefined)
    },
    sellerAccountsList: function () {
      var af = this.buyboxFilterData && this.buyboxFilterData.availableFilters
      if (!af || !af.sellerAccounts || af.sellerAccounts.length === 0) return []
      return af.sellerAccounts.map(function (sa) {
        return { sellerId: sa.id, accountNickName: sa.name }
      })
    },
    filteredCountries: function () {
      return buildMarketplaceFilterDropdownItems(this.filterUserCountries, this.filterCountriesAll)
    },
    alertCounts: function () {
      return {
        suppressed: this.allAlerts.filter(function (a) { return a.statusType === 'suppressed' }).length,
        lost: this.allAlerts.filter(function (a) { return a.statusType === 'lost' }).length,
        contested: this.allAlerts.filter(function (a) { return a.statusType === 'contested' }).length,
        potential: this.allAlerts.filter(function (a) { return a.impactClass === 'positive' }).length
      }
    },
    startIndex: function () {
      return (this.currentPage - 1) * this.itemsPerPage
    },
    endIndex: function () {
      return Math.min(this.startIndex + this.itemsPerPage, this.totalAlerts)
    },
    paginatedAlerts: function () {
      return this.allAlerts
    },
    visiblePages: function () {
      var pages = []
      var maxVisible = 5
      var start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2))
      var end = Math.min(this.totalPages, start + maxVisible - 1)

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1)
      }

      for (var i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    }
  },
  methods: {
    alertsFilterParams: function () {
      var params = {}
      if (this.filterByMarket.length > 0) params.marketplaces = this.filterByMarket.join(',')
      if (this.filterBySellAcc.length > 0) params.sellerAccounts = this.filterBySellAcc.join(',')
      return params
    },
    exportCsv: function () {
      if (this.exporting) return
      var self = this
      var params = Object.assign({}, this.alertsFilterParams())
      if (this.filterStatus !== 'all') params.status = this.filterStatus
      if (this.searchQuery && this.searchQuery.trim()) params.search = this.searchQuery.trim()
      if (this.sortBy) params.sortBy = this.sortBy
      if (this.sortDir) params.order = this.sortDir

      var qs = Object.keys(params).map(function (key) {
        return encodeURIComponent(key) + '=' + encodeURIComponent(params[key])
      }).join('&')
      var exportUrl = '/api/v1/buybox/dashboard/issues/export' + (qs ? '?' + qs : '')
      var defaultName = 'buybox-issues-' + new Date().toISOString().slice(0, 10) + '.csv'

      this.exporting = true
      var xhr = new XMLHttpRequest()
      xhr.open('GET', exportUrl, true)
      xhr.responseType = 'blob'
      xhr.onload = function () {
        self.exporting = false
        if (xhr.status === 200) {
          var blob = xhr.response
          var url = window.URL.createObjectURL(blob)
          var link = document.createElement('a')
          link.href = url
          var cd = xhr.getResponseHeader('Content-Disposition')
          var filename = defaultName
          if (cd) {
            var m = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
            if (m && m[1]) filename = m[1].replace(/['"]/g, '').trim() || defaultName
          }
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        } else {
          var reader = new FileReader()
          reader.onload = function () {
            var errorMsg = 'Failed to export issues. Please try again.'
            try {
              var errorJson = JSON.parse(reader.result)
              errorMsg = errorJson.message || errorJson.error || errorMsg
            } catch (e) { /* not JSON */ }
            alert(errorMsg)
          }
          reader.readAsText(xhr.response)
        }
      }
      xhr.onerror = function () {
        self.exporting = false
        alert('Failed to export issues. Please try again.')
      }
      xhr.send()
    },
    getMarketplaceSuffix: function (country) {
      return getMarketplaceAmazonSuffix(country)
    },
    isSellerSelected: function (sellerId) {
      if (!this.filterBySellAcc || this.filterBySellAcc.length === 0) return false
      return this.filterBySellAcc.some(function (id) { return String(id) === String(sellerId) })
    },
    loadBuyboxFilters: function () {
      var self = this
      this.loadingFilters = true
      Promise.all([
        self.$http.get('/api/sentinel/products', { params: { productsOnly: true, page: 0, pageSize: 1 } }),
        self.$http.get('/api/v1/sellers').catch(function () { return { body: null } })
      ]).then(function (results) {
        self.loadingFilters = false
        var body = (results[0] && results[0].body) || {}
        var sellersBody = (results[1] && results[1].body) || {}
        var fromV1 = mapV1SellersToFilterAccounts(extractV1SellersItems(sellersBody))
        var fromProducts = (body.availableFilters && body.availableFilters.sellerAccounts) || []
        var sellerAccounts = fromV1.length > 0
          ? fromV1.map(function (r) { return { id: r.id, name: r.name } })
          : fromProducts.map(function (sa) {
            return { id: sa.id, name: sa.name != null ? sa.name : ('Account ' + sa.id) }
          })
        var marketplaces = (body.availableFilters && body.availableFilters.marketplaces) || []
        self.buyboxFilterData.availableFilters = { marketplaces: marketplaces, sellerAccounts: sellerAccounts }
        self.filterUserCountries = buildMarketplaceFilterCodes({
          fromAvailableFilters: marketplaces,
          v1SellersBody: sellersBody,
          canonicalOrder: self.filterCountriesAll.filter(function (c) { return c !== 's' })
        })
        if (self.filterByMarket.length > 0) {
          var mpSet = new Set(self.filterUserCountries)
          self.filterByMarket = self.filterByMarket.filter(function (x) { return mpSet.has(x) })
        }
        if (sellerAccounts.length > 0 && self.filterBySellAcc.length > 0) {
          var idSet = new Set(sellerAccounts.map(function (sa) { return String(sa.id) }))
          self.filterBySellAcc = self.filterBySellAcc.filter(function (id) { return idSet.has(String(id)) })
        }
        self.$nextTick(function () {
          self.initializeFilterDropdowns()
        })
      }, function () {
        self.loadingFilters = false
        self.buyboxFilterData.availableFilters = null
        self.filterUserCountries = []
      })
    },
    initializeFilterDropdowns: function () {
      var self = this
      if (this.$refs.saDropdown) {
        try { $(this.$refs.saDropdown).dropdown('destroy') } catch (e) { /* not yet initialized */ }
        $(this.$refs.saDropdown).dropdown({
          onChange: function (v) {
            if (self._syncingDropdowns) return
            self.filterBySellAcc = v === '' ? [] : v.split(',').map(Number)
            self.currentPage = 1
            self.fetchIssues()
            self.syncAlertsRouteQuery()
          },
          placeholder: 'All Accounts',
          fullTextSearch: true
        })
      }
      if (this.$refs.marketplaceDropdown) {
        try { $(this.$refs.marketplaceDropdown).dropdown('destroy') } catch (e) { /* not yet initialized */ }
        $(this.$refs.marketplaceDropdown).dropdown({
          onChange: function (v) {
            if (self._syncingDropdowns) return
            self.filterByMarket = v === '' ? [] : v.split(',')
            self.currentPage = 1
            self.fetchIssues()
            self.syncAlertsRouteQuery()
          },
          placeholder: 'All Marketplaces',
          fullTextSearch: true
        })
      }
    },
    buildAlertsRouteQuery: function () {
      var o = {}
      if (this.filterByMarket.length) o.marketplaces = this.filterByMarket.join(',')
      if (this.filterBySellAcc.length) o.sellerAccounts = this.filterBySellAcc.map(String).join(',')
      return o
    },
    syncAlertsRouteQuery: function () {
      if (this._applyingAlertsRouteQuery || this.$route.path !== '/all-alerts') return
      var next = this.buildAlertsRouteQuery()
      if (shallowEqualAlertsQuery(next, this.$route.query)) return
      this.$router.replace({ path: '/all-alerts', query: next })
    },
    applyAlertsRouteQuery: function (q) {
      if (!q || typeof q !== 'object') return false
      var has = false
      for (var i = 0; i < ALERTS_URL_KEYS.length; i++) {
        var k = ALERTS_URL_KEYS[i]
        if (q[k] != null && q[k] !== '') {
          has = true
          break
        }
      }
      if (!has) return false

      this._applyingAlertsRouteQuery = true
      try {
        if (!Object.prototype.hasOwnProperty.call(q, 'marketplaces')) this.filterByMarket = []
        if (!Object.prototype.hasOwnProperty.call(q, 'sellerAccounts')) this.filterBySellAcc = []

        if (Object.prototype.hasOwnProperty.call(q, 'marketplaces') && q.marketplaces) {
          this.filterByMarket = String(q.marketplaces).split(',').map(function (s) { return s.trim() }).filter(Boolean)
        }
        if (Object.prototype.hasOwnProperty.call(q, 'sellerAccounts') && q.sellerAccounts) {
          this.filterBySellAcc = String(q.sellerAccounts).split(',').map(function (s) {
            var t = s.trim()
            if (!t) return null
            var n = Number(t)
            return isNaN(n) ? t : n
          }).filter(function (id) { return id != null && id !== '' })
        }
      } finally {
        var self = this
        this.$nextTick(function () {
          self._applyingAlertsRouteQuery = false
        })
      }
      return true
    },
    getConfidenceDotClass: function(statusType) {
      if (statusType === 'suppressed') return 'high'
      if (statusType === 'lost') return 'medium'
      if (statusType === 'contested') return 'low'
      return 'medium'
    },
    sortHeadClass: function (col) {
      return {
        'sort-col-active': this.sortBy === col,
        'sort-col-asc': this.sortBy === col && this.sortDir === 'asc',
        'sort-col-desc': this.sortBy === col && this.sortDir === 'desc'
      }
    },
    onSort: function (column) {
      if (ALERTS_TABLE_SORT_KEYS.indexOf(column) === -1) return
      if (this.sortBy !== column) {
        this.sortBy = column
        this.sortDir = column === 'status' ? 'asc' : 'desc'
      } else {
        this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc'
      }
      this.currentPage = 1
      this.fetchIssues()
    },
    fetchIssues: function () {
      var self = this
      self.loading = true
      self.error = null

      var params = Object.assign({}, self.alertsFilterParams(), {
        page: self.currentPage,
        pageSize: self.itemsPerPage
      })

      if (self.filterStatus !== 'all') {
        params.status = self.filterStatus
      }

      if (self.searchQuery && self.searchQuery.trim()) {
        params.search = self.searchQuery.trim()
      }

      if (self.sortBy) {
        params.sortBy = self.sortBy
      }
      if (self.sortDir) {
        params.order = self.sortDir
      }

      self.$http.get('/api/v1/buybox/dashboard/issues', { params: params }).then(function (response) {
        if (response.body) {
          if (response.body.totalItems !== undefined) {
            self.totalAlerts = response.body.totalItems || 0
            self.totalPages = response.body.totalPages || 1
          } else if (response.body.meta) {
            self.totalAlerts = response.body.meta.total || 0
            self.totalPages = response.body.meta.total_pages || 1
          } else {
            self.totalAlerts = self.allAlerts.length
            self.totalPages = Math.ceil(self.totalAlerts / self.itemsPerPage)
          }
          
          self.mapApiDataToAlerts(response.body)
        }
        self.loading = false
        self.lastUpdateTime = Date.now()
        self.lastUpdated = 'just now'
      }, function (err) {
        console.error('[AllAlertsPage] Failed to fetch issues:', err)
        var errorMsg = 'Failed to load issues'
        if (err.body && err.body.message) {
          errorMsg = err.body.message
        } else if (err.message) {
          errorMsg = err.message
        }
        self.error = errorMsg
        self.loading = false
      })
    },

    mapApiDataToAlerts: function (data) {
      var self = this
      var items = []
      if (data.watchdogs && Array.isArray(data.watchdogs)) {
        items = data.watchdogs
      } else if (data.priority_issues && data.priority_issues.items) {
        items = data.priority_issues.items
      } else if (data.items && Array.isArray(data.items)) {
        items = data.items
      } else if (Array.isArray(data)) {
        items = data
      }

      if (!Array.isArray(items)) {
        items = []
      }

      this.allAlerts = items.map(function (item, index) {
        var statusType = self.mapStatusType(item.status)
        var impactDisplay = item.estimated_daily_loss_display || '-$0'
        if (statusType === 'owned' && impactDisplay && String(impactDisplay).indexOf('-') === 0) {
          impactDisplay = String(impactDisplay).replace(/^-/, '').trim() || '$0'
        }
        return {
          id: item.product_id || item.id || ('ALT-' + (index + 1)),
          product: item.name || item.product_name || item.asin || 'Unknown Product',
          asin: item.asin || '',
          imageUrl: item.image_url || null,
          status: item.status_label || item.status || 'UNKNOWN',
          statusType: statusType,
          description: self.buildDescription(item),
          badge: self.getBadge(item),
          badgeClass: self.getBadgeClass(item),
          impact: impactDisplay,
          impactClass: self.getImpactClass(item, statusType),
          actionButton: item.needs_action ? (item.action_label || 'View Details') : null,
          actionButtonClass: item.action_type === 'analyze' ? 'secondary' : 'primary',
          timeAgo: self.formatTimeAgo(item.last_updated || item.created_at),
          productId: item.product_id,
          ownPrice: item.own_price_display,
          competitorPrice: item.competitor_price_display,
          priceGap: item.price_gap_display,
          buyboxWinner: item.buybox_winner,
          confidence: item.confidence
        }
      })
    },

    mapStatusType: function (status) {
      if (!status) return 'lost'
      var statusLower = status.toLowerCase()
      if (statusLower.indexOf('suppress') >= 0) return 'suppressed'
      if (statusLower.indexOf('lost') >= 0 || statusLower.indexOf('competitor') >= 0) return 'lost'
      if (statusLower.indexOf('contest') >= 0) return 'contested'
      if (statusLower.indexOf('owned') >= 0 || statusLower.indexOf('win') >= 0) return 'owned'
      return 'lost'
    },

    buildDescription: function (item) {
      if (item.issue_description) {
        var desc = item.issue_description
        if (item.own_price_display && item.competitor_price_display) {
          desc += ' (You: ' + item.own_price_display + ' vs Competitor: ' + item.competitor_price_display + ')'
        }
        return desc
      }
      if (item.description) return item.description
      return 'No description available'
    },

    getBadge: function (item) {
      if (item.confidence && item.confidence.label) {
        return item.confidence.label.split(' - ')[0]
      }
      if (item.has_price_gap) {
        return '+ Price gap detected'
      }
      return null
    },

    getBadgeClass: function (item) {
      if (item.confidence && item.confidence.level) {
        if (item.confidence.level === 'high') return 'success'
        if (item.confidence.level === 'medium') return 'warning'
        if (item.confidence.level === 'low') return 'info'
      }
      if (item.has_price_gap) return 'warning'
      return 'info'
    },

    getImpactClass: function (item, statusType) {
      if (statusType === 'owned') return 'neutral'
      var loss = item.estimated_daily_loss_cents || 0
      if (loss > 0) return 'negative'
      if (loss < 0) return 'positive'
      var display = item.estimated_daily_loss_display || ''
      if (display.indexOf('+') >= 0) return 'positive'
      return 'negative'
    },

    formatTimeAgo: function (timestamp) {
      if (!timestamp) return 'recently'
      try {
        var date = new Date(timestamp)
        var now = new Date()
        var diff = Math.floor((now - date) / 1000)

        if (diff < 60) return diff + 's ago'
        if (diff < 3600) return Math.floor(diff / 60) + 'm ago'
        if (diff < 86400) return Math.floor(diff / 3600) + 'h ago'
        return Math.floor(diff / 86400) + 'd ago'
      } catch (e) {
        return 'recently'
      }
    },

    goBack: function () {
      this.$router.push('/home')
    },
    viewProductDetails: function (productId) {
      this.$router.push({
        path: '/product_details',
        query: buildProductDetailsQuery(productId, this.$route.fullPath)
      })
    },
    handleActionButton: function (alert) {
      this.viewProductDetails(alert.productId || alert.id)
    },
    previousPage: function () {
      if (this.currentPage > 1) {
        this.currentPage--
        this.fetchIssues()
        this.scrollToTop()
      }
    },
    nextPage: function () {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.fetchIssues()
        this.scrollToTop()
      }
    },
    goToPage: function (page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
        this.fetchIssues()
        this.scrollToTop()
      }
    },
    scrollToTop: function () {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    updateTimeAgo: function () {
      if (!this.lastUpdateTime) {
        this.lastUpdated = 'just now'
        return
      }

      var now = Date.now()
      var diff = Math.floor((now - this.lastUpdateTime) / 1000)

      if (diff < 60) {
        this.lastUpdated = diff + 's ago'
      } else if (diff < 3600) {
        var minutes = Math.floor(diff / 60)
        this.lastUpdated = minutes + 'm ago'
      } else {
        var hours = Math.floor(diff / 3600)
        this.lastUpdated = hours + 'h ago'
      }
    },
    startTimer: function () {
      var self = this
      this.timerInterval = setInterval(function () {
        self.updateTimeAgo()
      }, 1000)
    }
  },
  created: function () {
    this.applyAlertsRouteQuery(this.$route.query)
    this.fetchIssues()
  },
  mounted: function () {
    this.startTimer()
    this.loadBuyboxFilters()
    var self = this
    this.$nextTick(function () {
      self.initializeFilterDropdowns()
      self.syncAlertsRouteQuery()
    })
  },
  beforeDestroy: function () {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
    }
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },
  watch: {
    '$route': function (to, from) {
      if (to.path !== '/all-alerts') return
      if (this._applyingAlertsRouteQuery) return
      if (from && from.path === to.path && shallowEqualAlertsQuery(this.buildAlertsRouteQuery(), to.query)) return
      this.applyAlertsRouteQuery(to.query)
      this.currentPage = 1
      this.fetchIssues()
    },
    filterUserCountries: function () {
      this.$nextTick(function () {
        this.initializeFilterDropdowns()
      })
    },
    sellerAccountsList: function () {
      this.$nextTick(function () {
        this.initializeFilterDropdowns()
      })
    },
    filterStatus: function () {
      this.currentPage = 1
      this.fetchIssues()
    },
    searchQuery: function () {
      this.currentPage = 1
      // Debounce search to avoid too many API calls
      var self = this
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.searchTimeout = setTimeout(function () {
        self.fetchIssues()
      }, 500) // Wait 500ms after user stops typing
    }
  }
}
</script>



// WEBPACK FOOTER //
// AllAlertsPage.vue?4c26d4fc